/**
 * The dark bottom bar shown while table rows are selected ("N interviews
 * selected" · icon actions · Cancel). Extracted from the Sessions table's
 * FooterActionBar so other tables (Themes, theme excerpts) show the exact same
 * bar. It renders into the layout's SELECTION_ACTION_BAR_ROOT_ID slot (next to
 * FooterActionBar in NewPageLayout) so it sits in the same place on every page.
 */
import { useLayoutEffect, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";

export const SELECTION_ACTION_BAR_ROOT_ID = "selection-action-bar-root";

const ICON_STYLE = { color: "#FFFFFF", width: "20px", height: "20px" };

/** One icon action in the bar (white icon + tooltip). */
export function SelectionBarAction({
  title,
  icon: Icon,
  onClick,
  disabled = false,
}: {
  title: string;
  icon: React.ElementType;
  onClick: () => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <Tooltip title={title} placement="top">
      {/* span keeps the tooltip working on a disabled button */}
      <Box component="span" display="flex">
        <IconButton size="small" onClick={onClick} disabled={disabled} sx={{ "&.Mui-disabled": { opacity: 0.4 } }}>
          <Icon style={ICON_STYLE} />
        </IconButton>
      </Box>
    </Tooltip>
  );
}

export function SelectionActionBar({
  label,
  onCancel,
  children,
  id,
}: {
  /** e.g. "3 interviews selected" */
  label: string;
  onCancel: () => void;
  /** SelectionBarAction items */
  children: ReactNode;
  id?: string;
}): JSX.Element | null {
  // Resolve the layout slot after mount (it isn't in the DOM during the first render).
  const [root, setRoot] = useState<HTMLElement | null | undefined>(undefined);
  useLayoutEffect(() => setRoot(document.getElementById(SELECTION_ACTION_BAR_ROOT_ID)), []);
  if (root === undefined) return null;

  const bar = (
    <Box
      display="flex"
      position="absolute"
      bottom={16}
      right={16}
      zIndex={1200}
      padding="8px 16px"
      borderRadius={2}
      id={id}
      alignItems="center"
      width="calc(100% - 216px)"
      justifyContent="space-between"
      sx={{ backgroundColor: "#464C53" }}
    >
      <Box>
        <Typography fontWeight={700} fontSize="14px" color="#FFFFFF">
          {label}
        </Typography>
      </Box>
      <Box display="flex" columnGap={1}>
        {children}
        <Button
          onClick={onCancel}
          sx={{ textTransform: "none", color: "#FFFFFF", fontSize: "14px", fontWeight: 600 }}
          variant="text"
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
  return root ? createPortal(bar, root) : bar;
}
